import { useState } from "react"; // single-step form
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { growthHubSupabase, isGrowthHubConfigured } from "@/integrations/supabase/growthHubClient";
import { SERVICES, SERVICE_AREAS, COMPANY } from "@/lib/constants";
import { GEORGIA_SERVICE_AREAS } from "@/data/georgiaCities";
import { Phone, Send, Loader2, Lock } from "lucide-react";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { sendLeadToGrowthHub } from "@/lib/sendLeadToGrowthHub";
import FinancingCTA from "@/components/shared/FinancingCTA";
import { detectBrandProfile } from "@/lib/brandRegistry";

interface QuoteFormProps {
  sourcePage: string;
  variant?: "default" | "compact" | "hero";
  preselectedService?: string;
  preselectedCity?: string;
  defaultName?: string;
  defaultEmail?: string;
  defaultPhone?: string;
  defaultAddress?: string;
  extraNotes?: string;
  requireAddress?: boolean;
}

const QuoteForm = ({ 
  sourcePage, 
  variant = "default",
  preselectedService,
  preselectedCity,
  defaultName = "",
  defaultEmail = "",
  defaultPhone = "",
  defaultAddress = "",
  extraNotes,
  requireAddress = true,
}: QuoteFormProps) => {
  const { toast } = useToast();
  const { trackFormSubmission, trackPhoneClick } = useVisitorTracking();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFinancing, setShowFinancing] = useState(false);
  const [showDetails, setShowDetails] = useState(requireAddress);
  const [formData, setFormData] = useState({
    name: defaultName,
    email: defaultEmail,
    phone: defaultPhone,
    address: defaultAddress,
    city: preselectedCity || "",
    service_type: preselectedService || "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (requireAddress && !formData.address.trim()) {
      toast({
        title: "Property Address Required",
        description: "Please enter the property address so we can prepare your quote.",
        variant: "destructive",
      });
      setShowDetails(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Try Growth Hub first if configured, otherwise use local
      const client = isGrowthHubConfigured() ? growthHubSupabase : supabase;
      const brandProfile = detectBrandProfile();
      const leadPayload = {
        ...formData,
        source_page: sourcePage,
        source_brand: brandProfile.brand,
        source_hostname: typeof window !== "undefined" ? window.location.hostname : null,
      } as unknown as Record<string, unknown>;

      let submitError = null;
      const { error } = await client
        .from("crm_leads")
        .insert(leadPayload as never);

      if (error) {
        // If Growth Hub fails, try local as fallback
        if (isGrowthHubConfigured()) {
          console.debug('Growth Hub insert failed, trying local:', error);
          const { error: localError } = await supabase
            .from("crm_leads")
            .insert(leadPayload as never);
          submitError = localError;
        } else {
          submitError = error;
        }
      }

      if (submitError) throw submitError;

      // Send email notification (fire and forget - don't block on errors)
      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-lead-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          ...formData,
          source_page: sourcePage,
        }),
      }).catch(err => console.error('Email notification failed:', err));

      // Track form submission with Meta CAPI
      await trackFormSubmission('quote_form', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        source_page: sourcePage,
        service_type: formData.service_type,
        city: formData.city,
      });

      // Sync lead to Growth Hub approval queue
      await sendLeadToGrowthHub({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        service_interest: formData.service_type,
        notes: [formData.message, extraNotes].filter(Boolean).join(' | '),
        form_type: 'quote_form',
      });

      toast({
        title: "Quote Request Submitted!",
        description: "We'll contact you within 24 hours to discuss your roofing needs.",
      });

      setShowFinancing(true);
      setShowDetails(false);

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        city: preselectedCity || "",
        service_type: preselectedService || "",
        message: "",
      });

    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Error",
        description: "There was a problem submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (variant === "compact") {
    return (
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            placeholder="Your Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            type="tel"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
          />
        </div>
        <Input
          type="email"
          placeholder="Email Address *"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
        <Input
          type="text"
          placeholder="Property Address *"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          required
          autoComplete="street-address"
        />
        <Button type="submit" variant="accent" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Get Free Quote
        </Button>
      </form>
    );
  }

  return (
    <div className={variant === "hero" ? "" : "bg-card rounded-2xl p-6 md:p-8 shadow-card"}>
      {variant !== "hero" && (
        <div className="mb-6">
          <h3 className="text-xl font-heading font-bold text-foreground mb-2">
            Get Your Free Estimate
          </h3>
          <p className="text-muted-foreground text-sm">
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </div>
      )}


      <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              placeholder="Your Name *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Input
              type="tel"
              placeholder="Phone Number *"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
            />
            <Input
              type="email"
              placeholder="Email Address (optional)"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />

            {!showDetails && !requireAddress && (
              <button
                type="button"
                className="w-full text-center text-sm text-muted-foreground hover:text-accent underline underline-offset-2 transition-colors"
                onClick={() => setShowDetails(true)}
              >
                + Add project details (optional)
              </button>
            )}

            {showDetails && (
              <>
                <Input
                  placeholder={requireAddress ? "Property Address *" : "Property Address"}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required={requireAddress}
                />

                <div className="grid grid-cols-1 gap-4">
                  <Select
                    value={formData.city}
                    onValueChange={(value) => setFormData({ ...formData, city: value })}
                    aria-label="Select County/Area"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select County/Area" />
                    </SelectTrigger>
                    <SelectContent>
                      {sourcePage.startsWith("ga-") ? (
                        <>
                          {GEORGIA_SERVICE_AREAS.map((area) => (
                            <SelectItem key={area.slug} value={`${area.name} (${area.description})`}>
                              {area.name} — {area.description}
                            </SelectItem>
                          ))}
                        </>
                      ) : (
                        <>
                          {SERVICE_AREAS.map((area) => (
                            <SelectItem key={area.slug} value={area.name}>
                              {area.name}
                            </SelectItem>
                          ))}
                        </>
                      )}
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                    aria-label="Select Service Needed"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Service Needed" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((service) => (
                        <SelectItem key={service.slug} value={service.name}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={3}
                />
              </>
            )}

            <Button type="submit" variant="accent" size="lg" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <Send className="h-5 w-5 mr-2" />
              )}
              Get Your Free Quote
            </Button>

            <div className="flex items-center justify-center gap-1.5 text-muted-foreground text-xs pt-1">
              <Lock className="w-3 h-3" />
              <span>We never share your info • No obligation</span>
            </div>


        <div className="text-center pt-2">
          <span className="text-muted-foreground text-sm">Or call us now: </span>
          <a 
            href={COMPANY.phoneHref}
            className="text-accent font-semibold hover:underline"
            onClick={() => trackPhoneClick("quote_form")}
          >
            {COMPANY.phoneFormatted}
          </a>
        </div>
      </form>

      {showFinancing && (
        <div className="mt-6">
          <FinancingCTA variant="card" />
        </div>
      )}
    </div>
  );
};

export default QuoteForm;
